import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import Quiz from '../components/slides/activity/Quiz';
import type { Slide } from '../../utils/supabase/client';

export default function DemoQuiz() {
  const navigate = useNavigate();
  const [key, setKey] = useState(0);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<any>(null);

  // Demo slide data with multiple questions
  const demoSlide: Slide = {
    id: 'demo-quiz',
    lesson_id: 'demo',
    type: 'quiz',
    slide_order: 0,
    created_at: new Date().toISOString(),
    content: {
      timeLimit: 300, // 5 minutes
      points: 100,
      questions: [
        {
          question: 'What is the capital of France?',
          options: ['London', 'Berlin', 'Paris', 'Madrid'],
          correctAnswer: 2,
        },
        {
          question: 'Which planet is known as the Red Planet?',
          options: ['Venus', 'Mars', 'Jupiter', 'Saturn'],
          correctAnswer: 1,
        },
        {
          question: 'What is the chemical formula for water?',
          options: ['CO2', 'H2O', 'NaCl', 'O2'],
          correctAnswer: 1,
        },
        {
          question: 'Who wrote "Romeo and Juliet"?',
          options: ['Charles Dickens', 'William Shakespeare', 'Jane Austen', 'Mark Twain'],
          correctAnswer: 1,
        },
        {
          question: 'What is the largest ocean on Earth?',
          options: ['Atlantic Ocean', 'Indian Ocean', 'Arctic Ocean', 'Pacific Ocean'],
          correctAnswer: 3,
        },
        {
          question: 'What is the square root of 144?',
          options: ['10', '11', '12', '13'],
          correctAnswer: 2,
        },
        {
          question: 'Which gas do plants absorb from the atmosphere?',
          options: ['Oxygen', 'Nitrogen', 'Carbon Dioxide', 'Hydrogen'],
          correctAnswer: 2,
        },
        {
          question: 'In what year did World War II end?',
          options: ['1943', '1944', '1945', '1946'],
          correctAnswer: 2,
        },
        {
          question: 'What is the smallest prime number?',
          options: ['0', '1', '2', '3'],
          correctAnswer: 2,
        },
        {
          question: 'Which continent is the Sahara Desert located in?',
          options: ['Asia', 'Africa', 'Australia', 'South America'],
          correctAnswer: 1,
        },
      ],
    },
  };

  const handleSubmit = (answer: any) => {
    setHasSubmitted(true);
    setSelectedAnswer(answer);
    console.log('Quiz submitted:', answer);
  };

  const handleAnswerChange = (answer: any) => {
    setSelectedAnswer(answer);
  };

  const handleRestart = () => {
    setHasSubmitted(false);
    setSelectedAnswer(null);
    setKey(prev => prev + 1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-background to-indigo-50 dark:from-blue-950/20 dark:via-background dark:to-indigo-950/20">
      {/* Header */}
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" onClick={() => navigate('/demos')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Demos
            </Button>
            {hasSubmitted && (
              <Button variant="outline" onClick={handleRestart}>
                Retake Quiz
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Quiz Activity Demo</h1>
          <p className="text-muted-foreground">
            A comprehensive multi-question quiz with navigation, progress tracking, and optional time limits.
          </p>
        </div>

        <Quiz
          key={key}
          slide={demoSlide}
          onSubmit={handleSubmit}
          onAnswerChange={handleAnswerChange}
          selectedAnswer={selectedAnswer}
          hasSubmitted={hasSubmitted}
        />
      </div>
    </div>
  );
}
